import React, { useEffect, useState } from 'react'
import { Alert, Button, TextField, Typography } from '@mui/material'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import AppDialog from '../../../components/dialog/AppDialog'
import ConfirmDialog from '../../../components/dialog/ConfirmDialog'
import { apiUrl } from '../../../environments/environments'

class BadRequestClientError extends Error {}

const directRequestTemplate = JSON.stringify(
    {
        product: {
            kmat: '',
            configuration: [
                { charac: 'CST_JDE_CATALOG', value: '' },
                { charac: 'CST_JDE_CATALOG_2', value: '' },
                { charac: 'CST_JDE_CATALOG_3', value: '' },
            ],
        },
        language: 'EN',
        parts: [],
    },
    null,
    4
)

const getInitialDialogState = () => ({ request: directRequestTemplate, result: null, error: '', persist: true })

function DirectSapPriceDialog({ open, builder, onClose, onPersisted }) {
    const [request, setRequest] = useState(directRequestTemplate)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [persist, setPersist] = useState(true)
    const [confirmClose, setConfirmClose] = useState(false)

    useEffect(() => {
        if (open) {
            const initialState = getInitialDialogState()
            setRequest(initialState.request)
            setResult(initialState.result)
            setError(initialState.error)
            setPersist(initialState.persist)
            setConfirmClose(false)
        }
    }, [open])

    const closeDialog = () => {
        const initialState = getInitialDialogState()
        setRequest(initialState.request)
        setResult(initialState.result)
        setError(initialState.error)
        setPersist(initialState.persist)
        setConfirmClose(false)
        onClose()
    }

    const requestClose = () => {
        if (request !== directRequestTemplate) {
            setConfirmClose(true)
            return
        }
        closeDialog()
    }

    const getDirectPrice = async () => {
        setLoading(true)
        setError('')
        setResult(null)
        try {
            const body = JSON.parse(request)
            if (!body?.product?.kmat || !Array.isArray(body?.product?.configuration)) throw new BadRequestClientError()
            const { data } = await ApiService(
                `${apiUrl}/SAPDirect/${builder}/prices`,
                'post',
                body,
                null,
                0,
                persist ? { refresh: true } : { persist: false }
            )
            setResult(data)
            if (persist) await onPersisted()
        } catch (err) {
            if (err instanceof SyntaxError) {
                setError('This is not valid JSON. Paste the exact request payload sent by eCat.')
            } else if (err instanceof BadRequestClientError) {
                setError('The request must contain product.kmat and product.configuration, as sent by eCat.')
            } else {
                setError(err.response?.data?.error || 'SAP pricing is unavailable.')
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <AppDialog
            open={open}
            onClose={requestClose}
            title='Query SAP Direct Pricing'
            showCloseButton
            actions={
                <>
                    <Button onClick={requestClose}>Cancel</Button>
                    <Button variant='contained' onClick={getDirectPrice} disabled={loading}>
                        {loading ? 'Requesting...' : 'Query SAP'}
                    </Button>
                </>
            }>
            <Typography variant='body2' className='sap-direct-help'>
                Paste the exact request payload sent by eCat, including <code>product.kmat</code>,{' '}
                <code>product.configuration</code> and <code>parts</code>.
            </Typography>
            <TextField
                label='Request JSON'
                value={request}
                onChange={(event) => setRequest(event.target.value)}
                multiline
                minRows={12}
                fullWidth
                className='sap-direct-json'
            />
            <label className='sap-direct-checkbox'>
                <input type='checkbox' checked={persist} onChange={(event) => setPersist(event.target.checked)} /> Save response
            </label>
            {error && <Alert severity='error'>{error}</Alert>}
            {result && <Alert severity='success'>SAP price received{persist ? ' and saved.' : ''}</Alert>}
            {result && <pre className='sap-direct-price-json'>{JSON.stringify(result, null, 2)}</pre>}
            </AppDialog>
            <ConfirmDialog
                open={confirmClose}
                title='Discard request changes?'
                message='Your edited SAP request will be lost.'
                onCancel={() => setConfirmClose(false)}
                onConfirm={closeDialog}
                confirmLabel='Discard'
            />
        </>
    )
}

export default DirectSapPriceDialog